import { useEffect, useState, type ReactNode } from 'react';
import type { LegalDoc } from '@lc/contracts';
import { api, type LegalPage as Page } from '../api';
import { useI18n } from '../i18n';
import { Plate } from '../ui/chrome';

/** The support address, linked wherever a paragraph says {support}. */
function withSupport(text: string, email: string): ReactNode {
  const [before, after] = text.split('{support}');
  if (after === undefined) return text;
  return (
    <>
      {before}
      <a href={`mailto:${email}`}>{email}</a>
      {after}
    </>
  );
}

/** Privacy, terms and support on the website itself, in the visitor's language (the API keeps the text). */
export function LegalPage({ page }: { page: Page }) {
  const { t, lang } = useI18n();
  const [doc, setDoc] = useState<LegalDoc | null>(null);
  const [failed, setFailed] = useState(false);
  // A change of language keeps the page up until the other one is in, so nothing collapses in between.
  useEffect(() => {
    setFailed(false);
    let live = true;
    api.legal(page, lang).then(
      (d) => live && setDoc(d),
      () => live && setFailed(true),
    );
    return () => void (live = false);
  }, [page, lang]);
  useEffect(() => {
    if (doc) document.title = `${doc.title} · ${t('footer.copy').replace('© ', '')}`;
  }, [doc, t]);

  return (
    <div className="legal-page">
      <div className="wrap">
        {doc ? (
          <article className="card legal">
            <Plate as="h1" large>
              {doc.title}
            </Plate>
            {doc.updated ? <p className="legal-updated">{doc.updated}</p> : null}
            {doc.sections.map((s) => (
              <section key={s.h}>
                <h2>{s.h}</h2>
                {s.p.map((p) => (
                  <p key={p}>{withSupport(p, doc.supportEmail)}</p>
                ))}
              </section>
            ))}
          </article>
        ) : (
          <p className="legal-wait">{t(failed ? 'legal.failed' : 'loading')}</p>
        )}
      </div>
    </div>
  );
}
